import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'motion/react';

function ContributorOverview({
  comments,
  contributorId,
  expanded,
  variants,
  totalQuestions,
  sessionId
}) {
  const [completion, setCompletion] = useState(null);
  const [completedQuestions, setCompletedQuestions] = useState(0);

  useEffect(() => {
    // Get unique questions answered by counting distinct questionIds
    const uniqueQuestionsAnswered = new Set(
      comments
        .filter((comment) => comment.uid === contributorId)
        .map((comment) => comment.questionId)
    ).size;

    setCompletedQuestions(uniqueQuestionsAnswered);

    const completionPercentage = Math.min(
      (uniqueQuestionsAnswered / totalQuestions) * 100,
      100
    );
    setCompletion(completionPercentage);
  }, [comments, contributorId]);

  return (
    <ContributorOverviewContainer
      $expanded={expanded}
      key="contributor-overview"
      variants={variants}
      initial="hidden"
      animate="visible"
      exit="hidden">
      <AnimatePresence>
        <div>
          <h3>
            Session Completed:{' '}
            {expanded && (
              <span>
                {completedQuestions} / {totalQuestions}
              </span>
            )}
          </h3>
          <CompletionIndication $expanded={expanded}>
            <div>
              <span>{completion ? `${parseInt(completion, 10)}%` : '0%'}</span>
              <IndicationBar $expanded={expanded}>
                <Indicator
                  style={{
                    width: `${parseInt(completion, 10)}%`,
                    display: completion > 0 ? 'block' : 'none',
                    transition: 'width 1.5s ease-out'
                  }}
                />
                <Increments>
                  <IndicationIncrement />
                  <IndicationIncrement />
                  <IndicationIncrement />
                  <IndicationIncrement />
                  <IndicationIncrement />
                  <IndicationIncrement />
                  <IndicationIncrement />
                  <IndicationIncrement />
                  <IndicationIncrement />
                </Increments>
              </IndicationBar>
            </div>
          </CompletionIndication>
        </div>
      </AnimatePresence>
    </ContributorOverviewContainer>
  );
}

const ContributorOverviewContainer = styled(motion.div)`
  border-top: ${({ $expanded }) =>
    $expanded ? '1px solid var(--light-grey)' : 'none'};
  padding: ${({ $expanded }) => ($expanded ? '1.5rem 0' : '0 0 0.5rem 0')};
  width: 100%;

  > div {
    align-items: ${({ $expanded }) => ($expanded ? 'flex-start' : 'center')};
    display: flex;
    flex-direction: ${({ $expanded }) => ($expanded ? 'column' : 'row')};
    gap: ${({ $expanded }) => ($expanded ? '1rem' : '0.5rem')};
    width: 100%;

    h3 {
      display: flex;
      flex-wrap: nowrap;
      font-size: ${({ $expanded }) => ($expanded ? '1.25rem' : '0.875rem')};
      font-weight: 600;
      gap: 0.5rem;

      span {
        color: var(--blue);
        font-size: 1.25rem;
        font-weight: 600;
      }
    }
  }
`;

const CompletionIndication = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 100%;

  > div {
    align-items: center;
    display: flex;
    gap: 0.5rem;
    span {
      color: var(--blue);
      font-size: ${({ $expanded }) => ($expanded ? '2rem' : '0.875rem')};
      font-weight: 700;
    }
  }
`;

const IndicationBar = styled.div`
  background-color: var(--light-grey);
  border: 1px solid var(--blue);
  border-radius: 100px;
  display: grid;
  height: ${({ $expanded }) => ($expanded ? '24px' : '12px')};
  position: relative;
  width: 100%;
`;

const Increments = styled.div`
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 2;
`;

const IndicationIncrement = styled.div`
  border-right: 1px solid var(--blue);
`;

const Indicator = styled.div`
  background-color: var(--dark-blue);
  border-radius: 100px;
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  z-index: 1;
`;

export default ContributorOverview;
